import {observer} from "mobx-react-lite";
import {Collapse, IconButton, TableCell, TableRow, Tooltip} from "@mui/material";
import {ExpandMore, KeyboardArrowRight, Visibility} from "@mui/icons-material";
import {MithraTableCell} from "../../../../components/table/MithraTableCell";
import React, {useEffect} from "react";
import {PpvSubTable} from "./PpvSubTable";
import {getCollapseAnimationTiming} from "../../../../components/table/utils";
import {CurrencyComponent} from "../../../../components/currency-component/CurrencyComponent";
import {useStores} from "../../../../stores";
import {getPpvTableColumns} from "../classes/PpvColumns";
import type {PpvGroupRowState} from "../classes/PpvGroupRowState";
import {OpportunityStatusDropdown} from "../../../../pages/opportunity/components/OpportunityStatusDropdown";
import {OpportunityStatusEnum} from "../../../../services/classes/OpportunityClasses";
import {displayPpvVarianceKey} from "../classes/PpvConfig";
import {authStore} from "../../../../stores/init.ts";


type Props = {
    groupedRow: PpvGroupRowState
};
export const PpvTableMainRow: React.FC<Props> = observer(({groupedRow}) => {
    const {ppvControllerStore, p} = useStores();
    const hasActiveFilter = ppvControllerStore.ppvPageController.ppvPageFilterController.hasActiveFilter;
    const open = groupedRow.open;
    const COLUMNS = getPpvTableColumns(p, hasActiveFilter);
    const hasPartData = groupedRow.partStates !== undefined && groupedRow.partStates.length > 0;

    useEffect(() => {
        if (groupedRow.open && hasActiveFilter) {
            ppvControllerStore.ppvPageController.ppvPageDataController.fetchPpvUnfilteredCalculation(groupedRow);
        }
    }, [groupedRow, ppvControllerStore, groupedRow.open, hasActiveFilter]);
    const unfilteredPpvOpportunity = groupedRow.unfilteredCalculation?.ppv_opportunity ? Number(groupedRow.unfilteredCalculation.ppv_opportunity) : null;
    const currentPpvOpportunity = Number(groupedRow.data.ppv_opportunity);

    let i = 0;
    return <>
        <TableRow className="ppv-table-main-row">
            <MithraTableCell c={COLUMNS[i++]}
                             onClick={() => ppvControllerStore.ppvPageController.ppvPageDataController.onOpenClosePpvGroup(groupedRow)}>
                <IconButton aria-label="expand row" size="small">
                    {open ? <ExpandMore/> : <KeyboardArrowRight/>}
                </IconButton>
            </MithraTableCell>
            <MithraTableCell c={COLUMNS[i++]}>
                {/* ppv_key */}
                {groupedRow.data.p_name}
            </MithraTableCell>
            <MithraTableCell c={COLUMNS[i++]}>
                {displayPpvVarianceKey(p)}
            </MithraTableCell>

            <MithraTableCell c={COLUMNS[i++]}>
                {unfilteredPpvOpportunity !== null && (unfilteredPpvOpportunity !== Number(groupedRow.data.ppv_opportunity))
                    ? <Tooltip title="The selected opportunity value is different due to the applied filters">
                        <div>
                            <CurrencyComponent v={currentPpvOpportunity} nDigits={3}/>
                            {' / '}
                            <CurrencyComponent v={unfilteredPpvOpportunity} nDigits={3}/>
                        </div>
                    </Tooltip>
                    : <CurrencyComponent v={currentPpvOpportunity} nDigits={3}/>}
            </MithraTableCell>

            <MithraTableCell c={COLUMNS[i++]}>
                <Tooltip title={groupedRow.data.opportunity?.note || ''} arrow>
                <span>
                    {groupedRow.data.opportunity?.note && groupedRow.data.opportunity?.note.length > 50 ? `${groupedRow.data.opportunity?.note.substring(0, 50)}...` : groupedRow.data.opportunity?.note}
                </span>
                </Tooltip>
            </MithraTableCell>
            <MithraTableCell c={COLUMNS[i++]} style={{display: 'flex', alignItems: 'center'}}>
                {authStore.hasEditPermission ? <>
                    <div style={{marginRight: '8px', padding: '0px'}}>
                        <OpportunityStatusDropdown
                            status={groupedRow.data.opportunity?.status || OpportunityStatusEnum.PENDING_REVIEW}
                            setStatus={(status) => ppvControllerStore.changeStatus(groupedRow, status)}
                            loading={groupedRow.data.opportunity?.id === -1}
                        />
                    </div>
                    <IconButton onClick={() => {
                        ppvControllerStore.setPpvRowToEdit(groupedRow)
                        ppvControllerStore.setEditNoteDialogOpen(true)
                    }}>
                        <Visibility/>
                    </IconButton>
                </> : <OpportunityStatusDropdown
                    status={groupedRow.data.opportunity?.status || OpportunityStatusEnum.PENDING_REVIEW}
                    loading={groupedRow.data.opportunity?.id === -1}
                />}
            </MithraTableCell>
        </TableRow>

        <TableRow>
            <TableCell
                className={"cell-sub-table" + (hasPartData ? ' has-data' : '')}
                colSpan={COLUMNS.length}
                style={{paddingLeft: COLUMNS[0].width}}>
                {groupedRow.partStates !== undefined && groupedRow.partStates.length > 0 &&
                    <Collapse in={open} timeout={getCollapseAnimationTiming(groupedRow.partStates)} unmountOnExit>
                        <PpvSubTable groupedRow={groupedRow}/>
                    </Collapse>
                }
            </TableCell>
        </TableRow>
    </>
})
